import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from '@ublend-npm/aulaui-core';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AulaState, User } from '@core/types/state';
import {
  HANDIN_PROVIDER,
  LTI1P3_PROVIDER,
  QUIZZES_PROVIDER,
  TURNITIN_PROVIDER,
} from '@core/assignments/constants';
import NavigationBackHeader from '@app/components/presentational/common/NavigationBackHeader/NavigationBackHeader';
import pushRoute from '@app/utils/routing';
import { getAssignmentsBaseRoute } from '../utils/navigation';
import { createStyles } from './SubmissionsResponsive.styles';
import SubmissionSidebar from './SubmissionsSidebar/SubmissionSidebar';
import { ExtensionUser } from './ManageExtension/ManageExtension.types';

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

/**
 * Fallback: derive the submission segment from Redux routing state.
 * Used only when the router hasn't injected submissionId via params
 * (e.g. on the base `.../submissions` route).
 */
const selectCurrentSubmissionId = (state: AulaState): string => {
  const { routing } = state;
  if (!routing) return '';
  const { pathname } = routing.locationBeforeTransitions;
  const idx = pathname.search('/submissions');
  if (idx === -1) return '';
  const [segment] = pathname
    .substring(idx)
    .replace('/submissions', '')
    .replace(/^\//, '')
    .split('/');
  return segment || '';
};

// ---------------------------------------------------------------------------
// Dummy content — replace with real SubmissionDetails when ready
// ---------------------------------------------------------------------------
const DummySubmissionContent = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '120px',
    }}
  >
    <Typography variant="body1" color="text.secondary">
      Select a submission to view details.
    </Typography>
  </Box>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export type SubmissionAssignment = {
  description?: string;
  endDate?: string;
  gradesReleasedAt?: string;
  gradeType?: string;
  groupSet?: string;
  id: string;
  isHidden?: boolean;
  isAnonymised?: boolean;
  objectId?: string;
  parentAssignmentId?: string;
  provider?: string;
  shouldCloseOnDueDate?: boolean;
  startDate?: string;
  status?: string;
  students?: string[];
  studentsCount?: number;
  submissions?: string[];
  title: string;
};

type Props = {
  accessToken?: string;
  accessingAsStaff: boolean;
  assignment?: SubmissionAssignment;
  assignmentCreationError?: any;
  assignmentUpdateError?: any;
  createExtension: (params: any) => Promise<SubmissionAssignment | null>;
  editExtension: (params: any) => Promise<SubmissionAssignment | null>;
  extensions?: SubmissionAssignment[];
  fetchAssignments: (params: {
    accessToken: string;
    spaceId: string;
    user: User;
  }) => Promise<void>;
  fetchSubmissions: (params: {
    accessToken: string;
    assignmentId: string;
  }) => void;
  isFetchingAssignments: boolean;
  isEducator: boolean;
  loadStudents: (spaceId: string) => Promise<any[]>;
  params?: {
    assignmentId?: string;
    submissionId?: string;
  };
  spaceId: string;
  resetExtensionErrors: () => void;
  user: (User & ExtensionUser) | null;
};

const SubmissionsResponsive = ({
  accessToken,
  accessingAsStaff,
  assignment,
  assignmentCreationError,
  assignmentUpdateError,
  createExtension,
  editExtension,
  extensions = [],
  fetchAssignments,
  fetchSubmissions,
  isFetchingAssignments,
  isEducator,
  loadStudents,
  params,
  spaceId,
  resetExtensionErrors,
  user,
}: Props) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const {
    webAppFtSpaceArea202504,
    webappFtHandinAnonymousExtensions202110,
    webappFtTurnitinAnonymousExtensions202110,
  } = useFlags();
  const staffAlertOffset = webAppFtSpaceArea202504 && accessingAsStaff ? 56 : 0;
  const styles = createStyles(theme, staffAlertOffset);

  // Prefer the submissionId injected by the router (available immediately on
  // hard refresh) and fall back to parsing the Redux routing state (used when
  // landing on the base `.../submissions` route that has no :submissionId param).
  const reduxSubmissionId = useSelector(selectCurrentSubmissionId);
  const currentSubmissionId = params?.submissionId ?? reduxSubmissionId;
  const [selectedExtensionId, setSelectedExtensionId] = useState(
    assignment?.id || ''
  );

  useEffect(() => {
    if (assignment?.id && !selectedExtensionId) {
      setSelectedExtensionId(assignment.id);
    }
  }, [assignment?.id, selectedExtensionId]);

  const anonymousExtensionsEnabled = (() => {
    if (!assignment?.isAnonymised) return true;
    if (assignment.provider === HANDIN_PROVIDER) {
      return Boolean(webappFtHandinAnonymousExtensions202110);
    }
    if (assignment.provider === TURNITIN_PROVIDER) {
      return Boolean(webappFtTurnitinAnonymousExtensions202110);
    }
    return false;
  })();
  const canCreateExtensions = Boolean(
    assignment &&
      isEducator &&
      !accessingAsStaff &&
      !assignment.groupSet &&
      assignment.provider !== QUIZZES_PROVIDER &&
      assignment.provider !== LTI1P3_PROVIDER &&
      anonymousExtensionsEnabled
  );
  const canEditExtensions = isEducator;

  const refreshAssignments = async (savedExtension?: SubmissionAssignment) => {
    if (!accessToken || !spaceId || !user) return;
    await fetchAssignments({ accessToken, spaceId, user });
    if (savedExtension?.id) {
      // Match Submissionsv4's openExtension: make the saved extension active
      // and ensure its submissions are available immediately.
      const extensionId =
        savedExtension.provider === TURNITIN_PROVIDER
          ? savedExtension.objectId || savedExtension.id
          : savedExtension.id;
      setSelectedExtensionId(extensionId);
      fetchSubmissions({
        accessToken,
        assignmentId: extensionId,
      });
    }
  };

  useEffect(() => {
    if (
      assignment ||
      !accessToken ||
      !spaceId ||
      !user ||
      isFetchingAssignments
    ) {
      return;
    }

    fetchAssignments({ accessToken, spaceId, user });
  }, [
    accessToken,
    assignment,
    fetchAssignments,
    isFetchingAssignments,
    spaceId,
    user,
  ]);

  useEffect(() => {
    if (!accessToken) return;

    extensions.forEach(({ id }) => {
      fetchSubmissions({ accessToken, assignmentId: id });
    });
    // Extensions belong to the assignment, so a change of parent assignment
    // is the point at which this list needs to be fetched again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, assignment?.id]);

  const assignmentRoute = params?.assignmentId
    ? `${getAssignmentsBaseRoute(spaceId)}/${params.assignmentId}`
    : `${getAssignmentsBaseRoute(spaceId)}/list`;

  const submissionsBaseRoute = `${getAssignmentsBaseRoute(spaceId)}/${
    params?.assignmentId
  }/submissions`;

  const selectExtension = (extension: SubmissionAssignment) => {
    const extensionId =
      extension.provider === TURNITIN_PROVIDER
        ? extension.objectId || extension.id
        : extension.id;

    setSelectedExtensionId(extensionId);
    if (accessToken) {
      fetchSubmissions({ accessToken, assignmentId: extensionId });
    }
    pushRoute(`${submissionsBaseRoute}/${extensionId}`);
  };

  // Treat "no trailing segment" the same as "list" — this is the submissions
  // list view. A refresh on `.../submissions` should keep the list visible, not
  // redirect anywhere.
  const isListView = !currentSubmissionId || currentSubmissionId === 'list';

  // ── Desktop: sidebar + content always side-by-side ─────────────────────────
  if (isMdUp) {
    return (
      <Box sx={styles.desktopShell}>
        <Box sx={styles.leftWrapper}>
          <SubmissionSidebar
            accessToken={accessToken}
            assignment={assignment}
            assignmentCreationError={assignmentCreationError}
            assignmentUpdateError={assignmentUpdateError}
            backRoute={assignmentRoute}
            canCreateExtensions={canCreateExtensions}
            canEditExtensions={canEditExtensions}
            createExtension={createExtension}
            editExtension={editExtension}
            extensions={extensions}
            loadStudents={loadStudents}
            onExtensionSaved={refreshAssignments}
            onSelectExtension={selectExtension}
            resetExtensionErrors={resetExtensionErrors}
            selectedExtensionId={selectedExtensionId}
            spaceId={spaceId}
            user={user}
          />
        </Box>
        <Box sx={styles.contentArea}>
          <DummySubmissionContent />
        </Box>
      </Box>
    );
  }

  // ── Mobile list view ────────────────────────────────────────────────────────
  if (isListView) {
    return (
      <Box sx={{ height: '100%' }}>
        <SubmissionSidebar
          accessToken={accessToken}
          assignment={assignment}
          assignmentCreationError={assignmentCreationError}
          assignmentUpdateError={assignmentUpdateError}
          backRoute={assignmentRoute}
          canCreateExtensions={canCreateExtensions}
          canEditExtensions={canEditExtensions}
          createExtension={createExtension}
          editExtension={editExtension}
          extensions={extensions}
          loadStudents={loadStudents}
          onExtensionSaved={refreshAssignments}
          onSelectExtension={selectExtension}
          resetExtensionErrors={resetExtensionErrors}
          selectedExtensionId={selectedExtensionId}
          spaceId={spaceId}
          user={user}
        />
      </Box>
    );
  }

  // ── Mobile detail view ──────────────────────────────────────────────────────
  // Back button goes to the submissions list (`.../submissions/list`).
  // White container wraps the detail content, matching the Assignment detail
  // mobile layout (AssignmentResponsive → pageContent).
  const submissionsListRoute = `${submissionsBaseRoute}/list`;

  return (
    <Box sx={styles.mobilePage}>
      <Box sx={styles.backButtonWrapper}>
        <NavigationBackHeader route={submissionsListRoute} />
      </Box>
      <Box sx={styles.mobileContentWrapper}>
        <DummySubmissionContent />
      </Box>
    </Box>
  );
};

export default SubmissionsResponsive;
